import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAvatarUrl } from '../data/mockData';
import './DashboardLayout.css';

/* ── Nav configs per role ───────────────────────── */
const NAV = {
  mentor: [
    { path: '/dashboard/mentor',              icon: '🏠', label: 'Overview' },
    { path: '/dashboard/mentor/sessions',     icon: '📅', label: 'Sessions' },
    { path: '/dashboard/mentor/availability', icon: '🗓️', label: 'Availability' },
    { path: '/dashboard/mentor/earnings',     icon: '💰', label: 'Earnings' },
    { path: '/dashboard/mentor/events',       icon: '🎤', label: 'My Events' },
    { path: '/dashboard/mentor/services',     icon: '🎯', label: 'My Services' },
    { path: '/dashboard/mentor/profile',      icon: '👤', label: 'My Profile' },
    { path: '/dashboard/mentor/reviews',      icon: '⭐', label: 'Reviews' },
    { path: '/dashboard/mentor/share',        icon: '📤', label: 'Share & Grow' },
    { path: '/dashboard/mentor/security',     icon: '🔐', label: 'Security' },
  ],
  mentee: [
    { path: '/dashboard/mentee',              icon: '🏠', label: 'Overview' },
    { path: '/mentors',                       icon: '🔍', label: 'Find Mentors', external: true },
    { path: '/dashboard/mentee/sessions',     icon: '📅', label: 'My Sessions' },
    { path: '/dashboard/mentee/following',    icon: '❤️', label: 'Following & Saved' },
    { path: '/dashboard/mentee/certifications',icon: '🏆', label: 'Certifications' },
    { path: '/dashboard/mentee/billing',      icon: '💳', label: 'Billing' },
    { path: '/dashboard/mentee/support',      icon: '🎫', label: 'Support' },
    { path: '/dashboard/mentee/refer',        icon: '🎁', label: 'Refer & Earn' },
    { path: '/dashboard/mentee/profile',      icon: '👤', label: 'My Profile' },
    { path: '/dashboard/mentee/security',     icon: '🔐', label: 'Security' },
  ],
  admin: [
    { path: '/dashboard/admin',               icon: '📊', label: 'Overview' },
    { path: '/dashboard/admin/users',         icon: '👥', label: 'Users' },
    { path: '/dashboard/admin/bookings',      icon: '📅', label: 'Bookings' },
    { path: '/dashboard/admin/finance',       icon: '💰', label: 'Finance' },
    { path: '/dashboard/admin/approvals',     icon: '✅', label: 'Approvals', badge: 34 },
    { path: '/dashboard/admin/certifications',icon: '🏆', label: 'Certifications' },
    { path: '/dashboard/admin/events',        icon: '📅', label: 'Events' },
    { path: '/dashboard/admin/features',      icon: '🚩', label: 'Feature Flags' },
    { path: '/dashboard/admin/email',         icon: '📧', label: 'Email' },
    { path: '/dashboard/admin/integrations',  icon: '🔌', label: 'Integrations' },
    { path: '/dashboard/admin/support',       icon: '🎫', label: 'Support', badge: 18 },
    { path: '/dashboard/admin/content',       icon: '📝', label: 'Content' },
    { path: '/dashboard/admin/settings',      icon: '⚙️', label: 'Settings' },
  ],
};

const ROLE_COLORS = { mentor: '#16a34a', mentee: '#2563eb', admin: '#7c3aed' };
const ROLE_LABELS = { mentor: 'Mentor', mentee: 'Mentee', admin: 'Admin' };

export default function DashboardLayout({ children, activeSection }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const navItems = NAV[user?.role] || [];
  const roleColor = ROLE_COLORS[user?.role] || '#1DB954';
  const avatarUrl = user?.photoUrl || getAvatarUrl(user?.avatar, user?.avatarBg, 80);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    if (path === `/dashboard/${user?.role}`) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="db-root">
      {/* ── Sidebar ── */}
      <aside className={`db-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileSidebarOpen ? 'mobile-open' : ''}`}>
        {/* Logo */}
        <div className="db-sidebar-logo">
          <Link to="/" className="db-logo-link">
            <div className="db-logo-mark">D</div>
            {!sidebarCollapsed && <span className="db-logo-text">Dev<span>Brains</span></span>}
          </Link>
          <button className="db-collapse-btn" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
            {sidebarCollapsed ? '›' : '‹'}
          </button>
        </div>

        {/* User card */}
        {!sidebarCollapsed && (
          <div className="db-user-card" style={{ borderLeftColor: roleColor }}>
            <div className="db-uc-av-wrap">
              <img src={avatarUrl} alt={user?.name} className="db-uc-avatar"
                onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
              <div className="db-uc-avatar-fb" style={{ background: `#${user?.avatarBg}` }}>{user?.initials}</div>
              <span className="db-online-dot" />
            </div>
            <div className="db-uc-info">
              <div className="db-uc-name">{user?.name}</div>
              <div className="db-uc-role" style={{ color: roleColor }}>
                {ROLE_LABELS[user?.role]}
              </div>
            </div>
          </div>
        )}

        {/* Nav items */}
        <nav className="db-nav">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`db-nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => setMobileSidebarOpen(false)}
              title={sidebarCollapsed ? item.label : ''}
            >
              <span className="db-nav-ico">{item.icon}</span>
              {!sidebarCollapsed && (
                <>
                  <span className="db-nav-label">{item.label}</span>
                  {item.badge && <span className="db-nav-badge">{item.badge}</span>}
                </>
              )}
            </Link>
          ))}
        </nav>

        {/* Bottom actions */}
        <div className="db-sidebar-bottom">
          <Link to="/" className="db-nav-item" title="Public Site">
            <span className="db-nav-ico">🌐</span>
            {!sidebarCollapsed && <span className="db-nav-label">Public Site</span>}
          </Link>
          <button className="db-nav-item db-logout-btn" onClick={handleLogout} title="Log Out">
            <span className="db-nav-ico">🚪</span>
            {!sidebarCollapsed && <span className="db-nav-label">Log Out</span>}
          </button>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileSidebarOpen && (
        <div className="db-mobile-overlay" onClick={() => setMobileSidebarOpen(false)} />
      )}

      {/* ── Main area ── */}
      <div className="db-main">
        {/* Top header */}
        <header className="db-topbar">
          <div className="db-topbar-left">
            <button className="db-hamburger" onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}>☰</button>
            <div className="db-breadcrumb">
              <span className="db-bc-role">{ROLE_LABELS[user?.role]} Portal</span>
              {activeSection && (
                <>
                  <span className="db-bc-sep">/</span>
                  <span className="db-bc-page">{activeSection}</span>
                </>
              )}
            </div>
          </div>
          <div className="db-topbar-right">
            <button className="db-topbar-btn" title="Notifications">
              🔔
              <span className="db-notif-dot" />
            </button>
            <div className="db-topbar-user" onClick={() => navigate(`/dashboard/${user?.role}/profile`)}>
              <img src={avatarUrl} alt={user?.name} className="db-topbar-avatar"
                onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
              <div className="db-topbar-avatar-fb" style={{ background: `#${user?.avatarBg}` }}>{user?.initials}</div>
              <div className="db-topbar-info">
                <span className="db-topbar-name">{user?.name}</span>
                <span className="db-topbar-role" style={{ color: roleColor }}>{ROLE_LABELS[user?.role]}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="db-content">
          {children}
        </main>
      </div>
    </div>
  );
}
